import React, { useState } from 'react';
import { useBusinessProfile } from '../contexts/BusinessProfileContext';
import { 
  Building2, 
  ChevronDown, 
  Plus, 
  Check,
  Settings,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const BusinessProfileSelector = () => {
  const { 
    profiles, 
    currentProfile, 
    loading, 
    switchProfile, 
    createProfile 
  } = useBusinessProfile();
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreateProfile = async () => {
    if (!newProfileName.trim()) {
      toast.error('Please enter a business name');
      return;
    }
    
    setCreating(true);
    try {
      const profile = await createProfile(newProfileName.trim());
      toast.success(`Created "${profile.name}"`);
      setShowCreateDialog(false);
      setNewProfileName('');
      // Switch to the new profile
      switchProfile(profile.id);
    } catch (err) {
      toast.error(err.message || 'Failed to create profile');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        <span className="text-sm text-gray-400">Loading...</span>
      </div>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors border border-gray-700">
            <Building2 className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-white max-w-[150px] truncate">
              {currentProfile?.name || 'Select Business'}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-gray-900 border-gray-700">
          <DropdownMenuLabel className="text-gray-400 text-xs">
            Your Businesses
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-gray-700" />
          
          {profiles.map((profile) => (
            <DropdownMenuItem
              key={profile.id}
              onClick={() => switchProfile(profile.id)}
              className="flex items-center justify-between cursor-pointer hover:bg-gray-800"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                <div>
                  <div className="text-sm text-white">{profile.name}</div>
                  {profile.phoneNumbers?.length > 0 && (
                    <div className="text-xs text-gray-500">
                      {profile.phoneNumbers.length} phone number{profile.phoneNumbers.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
              {currentProfile?.id === profile.id && (
                <Check className="w-4 h-4 text-green-400" />
              )}
            </DropdownMenuItem>
          ))}
          
          <DropdownMenuSeparator className="bg-gray-700" />
          
          <DropdownMenuItem
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 text-purple-400"
          >
            <Plus className="w-4 h-4" />
            <span>Add New Business</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create New Business Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Business</DialogTitle>
            <DialogDescription className="text-gray-400">
              Create a new business profile with its own phone numbers, settings, and integrations.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Business Name</Label>
              <Input
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
                placeholder="e.g., My Restaurant, Tech Support"
                className="bg-gray-800 border-gray-700 text-white"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateProfile()}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-gray-700 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProfile}
              disabled={creating || !newProfileName.trim()}
              className="bg-purple-600 hover:bg-purple-500"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Business'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BusinessProfileSelector;
